/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { NIFormData } from '../react/components/form/services';
import equal from "fast-deep-equal";
import { Store } from "redux";
import { combineEpics } from "redux-observable";
import { Observable, of } from "rxjs";
import { distinctUntilChanged, filter, map, switchMap, throttleTime } from "rxjs/operators";
import { isActionOf } from "typesafe-actions";
import { createEpic } from "../utils/epics";
import { syncSystem } from "./actions";
import { evalEpics, evalsReducer, EvalsState, EvaluationActions, scanUncreatedEvals, syncEvaluations } from "./evaluations";
import { facilitiesReducer, FacilitiesState, FacilityActions, facilityEpics, throttledGetFacilities } from "./facilities";
import { FollowUpActions, followUpsReducer, FollowUpsState, followUpEpics, throttledSyncFollowUps } from "./followUps";
import { FormDefinitionActions, loadFormsActions, formEpics, formsReducer, FormsState, throttledSyncForms, ImageActions } from "./forms";
import { AppImageActions } from "./images/actions";
import imagesEpics from "./images/epics";
import { InterfaceActions, interfaceEpics, interfaceReducer, InterfaceState } from "./interface";
import { } from "./interface/reducers";
import { oneTimeEpics } from "./onetime";
import { SessionAction, sessionEpics, sessionReducer, SessionState } from "./session";
import { UserActions, usersEpics } from "./users";
import { CoopActions, CoopsState, coopEpics, coopsReducer, throttledGetCoops } from "./coops";
import { WmpActions, wmpsReducer, WmpsState, throttledGetWmps, wmpEpics } from './wmps';

export * from "./actions";
export * from "./coops";
export * from "./evaluations";
export * from "./facilities";
export * from "./followUps";
export * from "./forms";
export * from "./images/actions";
export * from "./interface";
export * from "./onetime";
export * from "./session";
export * from "./wmps";

export interface AppState {
  evals: EvalsState;
  forms: FormsState;
  session: SessionState;
  interface: InterfaceState;
  coops: CoopsState;
  facilities: FacilitiesState;
  followUps: FollowUpsState;
  wmps: WmpsState;
}

export function rootReducer(state: AppState | undefined, action: AppAction): AppState {
  return {
    evals: evalsReducer(state ? state.evals : undefined, action),
    forms: formsReducer(state ? state.forms : undefined, action),
    session: sessionReducer(state ? state.session : undefined, action),
    interface: interfaceReducer(state ? state.interface : undefined, action),
    facilities: facilitiesReducer(state ? state.facilities : undefined, action),
    coops: coopsReducer(state ? state.coops : undefined, action),
    followUps: followUpsReducer(state ? state.followUps : undefined, action),
    wmps: wmpsReducer(state ? state.wmps : undefined, action),
  };
}

export const rootEpic = combineEpics(
  evalEpics,
  sessionEpics,
  formEpics,
  imagesEpics,
  usersEpics,
  facilityEpics,
  coopEpics,
  wmpEpics,
  oneTimeEpics,
  interfaceEpics,
  followUpEpics,
  createEpic(action$ =>
    action$.pipe(
      filter(isActionOf(syncSystem)),
      throttleTime(5000),
      switchMap(() =>
        of(
          scanUncreatedEvals(),
          throttledSyncForms(),
          throttledGetFacilities(),
          throttledGetCoops(),
          throttledGetWmps(),
          syncEvaluations(),
          throttledSyncFollowUps(),
          loadFormsActions.request(),
        )
      )
    )
  )
);

export function followState(store: Store<AppState, AppAction>) {
  return new Observable<AppState>(function(observer) {
    observer.next(store.getState());

    const unsubscribe = store.subscribe(function() {
      observer.next(store.getState());
    });

    return unsubscribe;
  });
}

export function followEval(store: Store<AppState, AppAction>, id: string): Observable<NIFormData> {
  return followState(store).pipe(
    map(state => state.evals.items.getIn([id, "data"])),
    filter(ev => ev !== undefined),
    distinctUntilChanged((a, b) => equal(a, b))
  );
}

export type AppAction =
  | SessionAction
  | EvaluationActions
  | FormDefinitionActions
  | ImageActions
  | UserActions
  | FacilityActions
  | CoopActions
  | AppImageActions
  | InterfaceActions
  | FollowUpActions
  | WmpActions;
